/**
 * Email Gate Modal - Premium Email Capture for Journey Generation
 *
 * Shows when user tries to generate personalized roadmap.
 * Captures email before allowing generation.
 * Shows profile completeness to encourage complete data entry.
 */

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { useAppStore } from '@/store/useAppStore'
import {
  calculateProfileCompleteness,
  getCompletenessMessage,
  type ProfileCompleteness,
} from '@/lib/utils/profileCompleteness'
import { USE_RULE_ENGINE_ONLY } from '@/lib/mocks/mockJourneyResponse'

interface EmailGateModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (email: string) => void
  isLoading?: boolean
  remainingGenerations?: number
  dailyRemaining?: number
  weeklyRemaining?: number
  existingEmail?: string | null
  onChangeEmail?: () => void
  onCompleteProfile?: (step?: number) => void
}

// Completeness Progress Bar Component
function CompletenessBar({ completeness }: { completeness: ProfileCompleteness }) {
  const barColor =
    completeness.percentage >= 80
      ? 'bg-emerald-500'
      : completeness.percentage >= 50
        ? 'bg-amber-500'
        : 'bg-red-500'

  return (
    <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${completeness.percentage}%` }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`h-full rounded-full ${barColor}`}
      />
    </div>
  )
}

// Missing Item Chip Component
function MissingItemChip({
  label,
  impact,
  onClick,
}: {
  label: string
  impact: 'critical' | 'high' | 'medium'
  onClick?: () => void
}) {
  const colors = {
    critical: 'bg-red-50 text-red-700 border-red-200',
    high: 'bg-amber-50 text-amber-700 border-amber-200',
    medium: 'bg-slate-50 text-slate-600 border-slate-200',
  }

  const icons = {
    critical: '!',
    high: '+',
    medium: '+',
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs rounded-lg border ${colors[impact]} hover:opacity-80 transition-opacity`}
    >
      <span className="font-bold">{icons[impact]}</span>
      <span>{label}</span>
    </button>
  )
}

export function EmailGateModal({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  remainingGenerations,
  dailyRemaining,
  weeklyRemaining,
  existingEmail,
  onChangeEmail,
  onCompleteProfile,
}: EmailGateModalProps) {
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')

  const isReturningUser = !!existingEmail

  const appState = useAppStore((state) => state)

  const completeness = useMemo(() => {
    return calculateProfileCompleteness(appState)
  }, [appState])


  const validateEmail = (email: string) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return re.test(email)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!email.trim()) {
      setError('Please enter your email')
      return
    }

    if (!validateEmail(email)) {
      setError('Please enter a valid email')
      return
    }

    onSubmit(email.trim().toLowerCase())
  }

  const handleCompleteProfile = (step?: number) => {
    onClose()
    onCompleteProfile?.(step)
  }

  const handleSkip = () => {
    onClose()
  }

  const missingToShow = [
    ...completeness.criticalMissing.slice(0, 2),
    ...completeness.highImpactMissing.slice(0, 2),
  ].slice(0, 3)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto max-h-[90vh] overflow-y-auto"
          >
            <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-primary-600 via-primary-500 to-indigo-600 px-6 py-5 relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -translate-y-1/2 translate-x-1/2" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-1/2 -translate-x-1/2" />

                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 p-2 bg-white/20 hover:bg-white/30 rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>

                <div className="relative flex items-center gap-3">
                  <div className="w-11 h-11 bg-white/20 rounded-xl flex items-center justify-center">
                    <span className="text-2xl">🗺️</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold text-lg">Your Personalized Roadmap</h3>
                    <div className="flex items-center gap-2">
                      <span className="text-white/90 text-xs font-medium">Expert NRI Tax Intelligence</span>
                      <span className="px-2 py-0.5 bg-amber-400 text-amber-900 text-[10px] font-bold rounded-full">
                        FREE
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Returning User - Simplified */}
              {isReturningUser ? (
                <div className="p-6">
                  {/* Rate Limit Warning - Only show when LLM mode is active (not Rule Engine) */}
                  {!USE_RULE_ENGINE_ONLY && (
                    <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-start gap-2">
                        <span className="text-lg">⚡</span>
                        <div>
                          <p className="text-sm font-bold text-amber-800">Make This Generation Count!</p>
                          <p className="text-xs text-amber-700 mt-0.5">
                            <span className="font-semibold">{dailyRemaining ?? remainingGenerations}/2</span> today
                            {weeklyRemaining !== undefined && (
                              <> • <span className="font-semibold">{weeklyRemaining}/3</span> this week</>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Completeness Section */}
                  <div className="mb-5">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-slate-700">Profile Completeness</span>
                      <span className={`text-sm font-bold ${
                        completeness.percentage >= 80 ? 'text-emerald-600' :
                        completeness.percentage >= 50 ? 'text-amber-600' : 'text-red-600'
                      }`}>{completeness.percentage}%</span>
                    </div>
                    <CompletenessBar completeness={completeness} />
                    <p className="text-xs text-slate-500 mt-2">{getCompletenessMessage(completeness)}</p>

                    {missingToShow.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {missingToShow.map((item) => (
                          <MissingItemChip
                            key={item.id}
                            label={item.label}
                            impact={item.impact}
                            onClick={() => handleCompleteProfile(item.wizardStep)}
                          />
                        ))}
                      </div>
                    )}

                    {onCompleteProfile && completeness.percentage < 80 && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleCompleteProfile()}
                        className="mt-3 w-full text-sm font-semibold"
                      >
                        Complete My Profile
                      </Button>
                    )}
                  </div>

                  {/* Email confirmation */}
                  <div className="text-center mb-4">
                    <p className="text-sm text-slate-500">
                      Using: <span className="font-semibold text-slate-700">{existingEmail}</span>
                    </p>
                  </div>

                  <Button
                    onClick={() => onSubmit(existingEmail!)}
                    disabled={isLoading || (!USE_RULE_ENGINE_ONLY && remainingGenerations === 0)}
                    className="w-full bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 text-white h-12 text-base font-bold rounded-xl shadow-lg shadow-primary-500/25 mb-3"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Generating...
                      </span>
                    ) : (
                      'Generate My Roadmap'
                    )}
                  </Button>

                  <button
                    type="button"
                    onClick={onChangeEmail}
                    className="w-full text-sm text-slate-500 hover:text-slate-700 transition-colors"
                  >
                    Use different email
                  </button>
                </div>
              ) : (
                /* New User - Simplified Clean Form */
                <form onSubmit={handleSubmit} className="p-6">
                  {/* Generation Count - Only show when LLM mode is active (not Rule Engine) */}
                  {!USE_RULE_ENGINE_ONLY && (
                    <div className="mb-6 text-center">
                      <div className="inline-flex items-center gap-3 px-5 py-3 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl">
                        <span className="text-3xl font-bold text-emerald-700">
                          {dailyRemaining ?? 2}/{weeklyRemaining ?? 3}
                        </span>
                        <div className="text-left">
                          <p className="text-sm font-semibold text-emerald-800">generations left</p>
                          <p className="text-xs text-emerald-600">today / this week</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Email Input - Primary Focus */}
                  <div className="mb-5">
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Enter your email to get started
                    </label>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value)
                        setError('')
                      }}
                      disabled={isLoading}
                      className={`w-full px-4 py-3.5 text-base border-2 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-colors ${
                        error ? 'border-red-300' : 'border-slate-200'
                      }`}
                    />
                    {error && <p className="mt-1.5 text-sm text-red-600 font-medium">{error}</p>}
                  </div>

                  {/* Submit */}
                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-primary-600 to-indigo-600 hover:from-primary-700 hover:to-indigo-700 text-white h-13 text-base font-bold rounded-xl shadow-lg shadow-primary-500/25"
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Generating...
                      </span>
                    ) : (
                      'Generate My Roadmap'
                    )}
                  </Button>

                  {/* Minimal Footer */}
                  <div className="mt-4 flex items-center justify-center gap-3 text-xs text-slate-400">
                    <span className="flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                      </svg>
                      Secure
                    </span>
                    <span>•</span>
                    <span>No spam</span>
                  </div>

                  <button
                    type="button"
                    onClick={handleSkip}
                    className="w-full mt-3 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    Skip for now
                  </button>
                </form>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

export default EmailGateModal
